/* global globalThis */
import { Alert, Button, Card, Form, Input, Layout, Progress, Space, Spin, Typography, message } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import APIService from "../Api/api.service";
import { LOGO_SVG_URL } from "../constants/common";
import { ROUTES } from "../constants/Route";

const { Content, Header } = Layout;
const { Title, Text } = Typography;

const READ_REQUEST_CODE_API_PATH = "Document/ReadRequestCode";
const VALIDATE_PASSKEY_API_PATH = "Document/ValidatePassKey";
const DOWNLOAD_API_PATH = "Document/Download";
const UPDATE_DOWNLOAD_STATUS_API_PATH = "Document/UpdateDownloadStatus";
const IPV4_LOOKUP_URL = "https://api4.ipify.org?format=json";
const IPV4_ADDRESS_PATTERN = /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;
const readRequestCodeRequests = new Map();

const isSuccessfulResponse = (result) => Boolean(result && !Array.isArray(result) && result.success === true && !result.status);

const readRequestCode = (apiservice, urlRequestCode) => {
  if (!readRequestCodeRequests.has(urlRequestCode)) {
    const request = apiservice
      .postRequest(READ_REQUEST_CODE_API_PATH, { requestCode: urlRequestCode })
      .finally(() => {
        readRequestCodeRequests.delete(urlRequestCode);
      });

    readRequestCodeRequests.set(urlRequestCode, request);
  }

  return readRequestCodeRequests.get(urlRequestCode);
};

const getAuthorizedJsonHeaders = (token) => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${token}`,
});

const postAuthorizedJson = async (apiservice, url, token, body) => {
  const response = await fetch(apiservice.getRequestUrl(url), {
    method: "post",
    headers: getAuthorizedJsonHeaders(token),
    body: JSON.stringify(body),
  });

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json();
  }

  return response;
};

const getRequestCodeFromUrl = () => {
  const queryString = globalThis.location.search;
  return decodeURIComponent(queryString.substring(1));
};

const getClientIpv4Address = async () => {
  try {
    const response = await fetch(IPV4_LOOKUP_URL);
    if (!response.ok) {
      return "";
    }

    const data = await response.json();
    return typeof data?.ip === "string" && IPV4_ADDRESS_PATTERN.test(data.ip) ? data.ip : "";
  } catch {
    return "";
  }
};

const getClientSystemDetails = async () => {
  const navigatorDetails = globalThis.navigator || {};
  const ipv4Address = await getClientIpv4Address();
  return [
    `IPv4Address: ${ipv4Address}`,
    `UserAgent: ${navigatorDetails.userAgent || ""}`,
    `Platform: ${navigatorDetails.platform || ""}`,
    `Language: ${navigatorDetails.language || ""}`,
  ].join("; ");
};

const getDownloadFileName = (contentDisposition) => {
  const fileNameMatch = contentDisposition?.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  return fileNameMatch ? decodeURIComponent(fileNameMatch[1].replaceAll("\"", "")) : "";
};

const readDownloadBlob = async (response, onProgress) => {
  const contentLength = Number(response.headers.get("content-length"));
  if (!response.body || !contentLength) {
    return response.blob();
  }

  const reader = response.body.getReader();
  const chunks = [];
  let receivedLength = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    chunks.push(value);
    receivedLength += value.length;
    onProgress(Math.min(99, Math.round((receivedLength / contentLength) * 100)));
  }

  return new Blob(chunks, { type: response.headers.get("content-type") || "application/octet-stream" });
};

const DocumentDownload = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [requestCode, setRequestCode] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [isRequestCodeValid, setIsRequestCodeValid] = useState(false);
  const [isPassKeyValid, setIsPassKeyValid] = useState(false);
  const [downloadPercent, setDownloadPercent] = useState(0);
  const [inlineMessage, setInlineMessage] = useState(null);
  const [apiservice] = useState(() => new APIService());
  const navigate = useNavigate();

  const callRequestCodeApi = useCallback(async (urlRequestCode) => {
    setIsRequestCodeValid(false);
    setIsPassKeyValid(false);
    setAuthToken("");

    if (!urlRequestCode) {
      navigate(ROUTES.DOCUMENT_ERROR);
      return;
    }

    setLoading(true);
    try {
      const result = await readRequestCode(apiservice, urlRequestCode);
      if (!isSuccessfulResponse(result)) {
        navigate(ROUTES.DOCUMENT_ERROR);
        return;
      }

      if (!result.token) {
        navigate(ROUTES.DOCUMENT_ERROR);
        return;
      }

      setAuthToken(result.token);
      setIsRequestCodeValid(true);
    } catch {
      navigate(ROUTES.DOCUMENT_ERROR);
    } finally {
      setLoading(false);
    }
  }, [apiservice, navigate]);

  useEffect(() => {
    const urlRequestCode = getRequestCodeFromUrl();
    setRequestCode(urlRequestCode);
    callRequestCodeApi(urlRequestCode);
  }, [callRequestCodeApi]);

  const handleValidatePassKey = async () => {
    try {
      setInlineMessage(null);
      const { passKey } = await form.validateFields(["passKey"]);
      setIsPassKeyValid(false);
      setLoading(true);
      if (!authToken) {
        navigate(ROUTES.DOCUMENT_ERROR);
        return;
      }

      const result = await postAuthorizedJson(apiservice, VALIDATE_PASSKEY_API_PATH, authToken, { passKey, requestCode });
      if (!isSuccessfulResponse(result)) {
        if (result?.isDisabled) {
          navigate(ROUTES.DOCUMENT_ERROR);
          return;
        }

        setInlineMessage({
          type: "error",
          text: result?.message || "Please verify passkey from email or call Provider1st at 855-514-2378.",
        });
        return;
      }

      setIsPassKeyValid(true);
      setDownloadPercent(0);
      setInlineMessage({ type: "success", text: "Passkey validated successfully." });
    } catch (error) {
      if (!error.errorFields) {
        setIsPassKeyValid(false);
        setInlineMessage({ type: "error", text: "Unable to validate passkey." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async ({ passKey }) => {
    setInlineMessage(null);

    if (!isRequestCodeValid) {
      setInlineMessage({
        type: "error",
        text: "Please verify link from email or call Provider1st at 855-514-2378.",
      });
      return;
    }

    if (!authToken) {
      navigate(ROUTES.DOCUMENT_ERROR);
      return;
    }

    setLoading(true);
    setDownloadPercent(0);
    try {
      const payload = { requestCode, passKey };
      const response = await fetch(apiservice.getRequestUrl(DOWNLOAD_API_PATH), {
        method: "post",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json, application/octet-stream",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || contentType.includes("application/json")) {
        const result = contentType.includes("application/json") ? await response.json() : null;
        setIsPassKeyValid(false);
        setInlineMessage({ type: "error", text: result?.message || "Unable to download document." });
        return;
      }

      setDownloadPercent(1);
      const blob = await readDownloadBlob(response, setDownloadPercent);
      const downloadUrl = globalThis.URL.createObjectURL(blob);
      const link = globalThis.document.createElement("a");
      const downloadFileName = getDownloadFileName(response.headers.get("content-disposition"));
      link.href = downloadUrl;
      if (downloadFileName) {
        link.download = downloadFileName;
      }
      globalThis.document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(downloadUrl);
      setDownloadPercent(100);

      const statusResult = await postAuthorizedJson(apiservice, UPDATE_DOWNLOAD_STATUS_API_PATH, authToken, {
        ...payload,
        clientSystemDetails: await getClientSystemDetails(),
      });

      if (!isSuccessfulResponse(statusResult)) {
        setInlineMessage({ type: "warning", text: "Download started, but status could not be updated." });
        return;
      }

      setInlineMessage({ type: "success", text: "Download completed successfully." });
    } catch {
      setIsPassKeyValid(false);
      setDownloadPercent(0);
      setInlineMessage({ type: "error", text: "Unable to download document." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout className="bg-white min-h-screen">
      <Header className="welcome-footer bg-white">
        <img
          alt="Provider1st Logo"
          className="mt-4 mx-auto"
          src={globalThis.location.origin + LOGO_SVG_URL}
        />
      </Header>
      <Spin spinning={loading} tip="Please wait...">
        <Content className="p-4 md:p-6">
          <div className="container mx-auto max-w-md">
            <Card className="mt-8 border-gray-300 shadow-sm">
              <Space direction="vertical" size={25} className="w-full">
                <div className="text-center">
                  <Title level={3} className="!mb-2">
                    Download Records
                  </Title>
                  <Text>Please validate your passkey to download the records.</Text>
                </div>

                {inlineMessage && (
                  <Alert
                    message={inlineMessage.text}
                    type={inlineMessage.type}
                    showIcon
                  />
                )}

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleDownload}
                  requiredMark={false}
                  className="w-full"
                >
                  <Form.Item
                    name="passKey"
                    style={{ marginBottom: 26 }}
                    rules={[{ required: true, message: "Please enter passkey." }]}
                  >
                    <Input
                      allowClear
                      placeholder="Enter passkey"
                      disabled={loading || !isRequestCodeValid}
                      onChange={() => {
                        setIsPassKeyValid(false);
                        setDownloadPercent(0);
                        setInlineMessage(null);
                      }}
                    />
                  </Form.Item>

                  <div className="text-center">
                    <Space>
                      <Button
                        onClick={handleValidatePassKey}
                        disabled={loading || !isRequestCodeValid}
                      >
                        Validate
                      </Button>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        disabled={loading || !isRequestCodeValid || !isPassKeyValid}
                      >
                        Download
                      </Button>
                    </Space>
                  </div>
                  {downloadPercent > 0 && (
                    <div className="mt-4 text-center">
                      <Progress
                        percent={downloadPercent}
                        status={downloadPercent === 100 ? "success" : "active"}
                      />
                    </div>
                  )}
                </Form>
              </Space>
            </Card>
          </div>
        </Content>
      </Spin>
    </Layout>
  );
};

export default DocumentDownload;
